import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { MediaViewerContentType } from '../../enums/media-viewer-content-types';
import { ImgViewerComponent } from '../img-viewer/img-viewer.component';
import { JsonViewerComponent } from '../jsonviewer/json-viewer.component';
import { PDFViewerComponent } from '../pdfviewer/pdfviewer.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';

@Component({
    selector: 'apcam-media-viewer-widget',
    templateUrl: './media-viewer-widget.component.html',
    styleUrl: './media-viewer-widget.component.scss',
    imports: [
        ImgViewerComponent,
        JsonViewerComponent,
        MatButtonModule,
        MatIconModule,
        MatToolbarModule,
        PDFViewerComponent,
        TooltipCustomComponent,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerWidgetComponent {
    readonly contentType = input.required<MediaViewerContentType>();
    readonly fileName = input.required<string>();
    readonly url = input.required<string>();

    readonly closeIcon = input('close');
    readonly closeLabel = input.required<string>();

    readonly showDownload = input(true);
    readonly showPrint = input(true);

    readonly close = output<void>();

    protected readonly MediaViewerContentType = MediaViewerContentType;

    /** Persistent triggers the active leaf viewer subscribes to on init. */
    protected readonly download$ = new Subject<void>();
    protected readonly print$ = new Subject<void>();

    /** Reset back to "not ready" whenever a new file is shown, then set by the leaf viewer. */
    protected readonly contentReady = linkedSignal<string, boolean>({
        source: this.url,
        computation: () => false,
    });

    /**
     * `JsonViewerComponent` has no `print` input at all, so printing JSON is not wired
     * hide print for JSON instead of showing a button that silently does nothing once enabled.
     */
    protected readonly printSupported = computed(
        () =>
            this.showPrint() &&
            this.contentType() !== MediaViewerContentType.APPLICATION_JSON
    );

    protected onContentLoaded(): void {
        this.contentReady.set(true);
    }

    protected onDownload(): void {
        this.download$.next();
    }

    protected onPrint(): void {
        this.print$.next();
    }
}
